/**
 * Synthesised sound cues per design.md §8. Naturalistic and sparing —
 * "never overstimulating or gamey." Six cues:
 *
 *   - playTap         button confirm / Done — soft felt-mallet
 *   - playTransition  phase handoff — gentle paper rustle
 *   - playCelebration end-of-session — whoosh + warm two-tone chime
 *   - playStickerReveal a single sticker landing — soft chime + place
 *   - playGentleChime second-chance "wrong again" — very neutral low tone
 *
 * Why synthesis over WAV files: keeps the bundle small, sidesteps
 * licence/attribution, and lets the cues stay tunable in a single
 * place. We can swap to recorded clips later by replacing the bodies —
 * the public surface is opaque about the mechanism (same shape as
 * lib/speech.ts).
 *
 * Mute gate:
 *   The module owns a process-wide muted flag, sync'd by
 *   SessionContext when parent settings load/change, and by
 *   ParentSettings's mute toggle. Each cue checks the flag first; no
 *   AudioContext is created when muted. Default = unmuted on boot
 *   (matches Settings.audioEnabled default).
 *
 * ─── iOS Safari unlock (matters for iPad) ───────────────────────────
 *
 * iOS Safari requires three things, all on the FIRST user gesture, or
 * Web Audio stays silent for the rest of the session:
 *
 *   1. The AudioContext must be created inside the gesture.
 *   2. `resume()` must be called *synchronously* inside the gesture
 *      (awaiting it moves execution to a microtask, outside the
 *      gesture window — the first audio you try to play after that
 *      won't be audible).
 *   3. A tiny silent buffer must be scheduled immediately so iOS opens
 *      the audio session for real. Without this, the context state
 *      goes to 'running' but the speaker stays inert until later cues.
 *
 * `unlockAudio()` does all three on the FIRST touchstart/click/keydown
 * anywhere in the document. By the time any cue function runs, the
 * context is fully unlocked.
 *
 * Also handled: SpeechSynthesis on iOS can put the AudioContext into
 * 'interrupted' state. Each cue checks for any non-'running' state and
 * resumes — covers 'suspended' AND 'interrupted' (the old code only
 * looked at 'suspended', which is why post-speech cues went silent on
 * iPad).
 */

// ─── Public surface ──────────────────────────────────────────────────

let muted = false;

/** Sync the mute flag from parent settings. SessionContext calls this
 *  on every settings change; ParentSettings also calls it directly on
 *  the mute toggle so the user hears (or stops hearing) the change
 *  immediately, without waiting for a refetch. */
export function setSoundMuted(value: boolean): void {
  muted = value;
}

export function playTap(): void {
  if (muted) return;
  const ctx = ensureContext();
  if (!ctx) return;
  resumeIfNeeded(ctx);

  // Felt-mallet style: low-mid sine, very short envelope. Two layered
  // tones at a fifth so it reads as "wood" rather than "beep".
  const now = ctx.currentTime;
  spawnTone(ctx, { freq: 520, type: "sine", peak: 0.18, attack: 0.002, decay: 0.07, start: now });
  spawnTone(ctx, { freq: 780, type: "sine", peak: 0.06, attack: 0.002, decay: 0.05, start: now });
}

export function playTransition(): void {
  if (muted) return;
  const ctx = ensureContext();
  if (!ctx) return;
  resumeIfNeeded(ctx);

  // Filtered noise — a single brief breath. The bandpass tunes it to
  // sound like turning a page, not switching a TV channel.
  const noise = makeNoise(ctx, 0.32);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 2200;
  bp.Q.value = 0.7;
  const gain = ctx.createGain();
  noise.connect(bp).connect(gain).connect(ctx.destination);
  applyEnvelope(ctx, gain, { attack: 0.01, hold: 0.04, decay: 0.22, peak: 0.07 });
  noise.start();
  noise.stop(ctx.currentTime + 0.35);
}

export function playCelebration(): void {
  if (muted) return;
  const ctx = ensureContext();
  if (!ctx) return;
  resumeIfNeeded(ctx);

  // Layer 1: airy whoosh under the confetti.
  const noise = makeNoise(ctx, 0.75);
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  const t0 = ctx.currentTime;
  lp.frequency.setValueAtTime(3800, t0);
  lp.frequency.exponentialRampToValueAtTime(380, t0 + 0.65);
  lp.Q.value = 0.9;
  const noiseGain = ctx.createGain();
  noise.connect(lp).connect(noiseGain).connect(ctx.destination);
  applyEnvelope(ctx, noiseGain, { attack: 0.06, hold: 0.08, decay: 0.55, peak: 0.08 }, t0);
  noise.start(t0);
  noise.stop(t0 + 0.8);

  // Layer 2: warm two-tone chime (C5 + E5 — major third, "handpan-ish").
  // Lands slightly after the whoosh starts so the ear catches both.
  const chimeStart = t0 + 0.18;
  spawnTone(ctx, {
    freq: 523.25, type: "sine", peak: 0.13, attack: 0.012, decay: 0.95, start: chimeStart,
  });
  spawnTone(ctx, {
    freq: 659.25, type: "sine", peak: 0.10, attack: 0.012, decay: 1.05, start: chimeStart + 0.06,
  });
}

export function playStickerReveal(): void {
  if (muted) return;
  const ctx = ensureContext();
  if (!ctx) return;
  resumeIfNeeded(ctx);

  const t0 = ctx.currentTime;
  // Bright but soft bell — single sine, G5.
  spawnTone(ctx, {
    freq: 783.99, type: "sine", peak: 0.12, attack: 0.012, decay: 0.6, start: t0,
  });

  // Subtle "paper place" click — short filtered-noise blip, just after.
  const noise = makeNoise(ctx, 0.08);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 1700;
  bp.Q.value = 1.2;
  const gain = ctx.createGain();
  noise.connect(bp).connect(gain).connect(ctx.destination);
  const blipStart = t0 + 0.04;
  applyEnvelope(ctx, gain, { attack: 0.005, hold: 0, decay: 0.07, peak: 0.05 }, blipStart);
  noise.start(blipStart);
  noise.stop(blipStart + 0.1);
}

export function playGentleChime(): void {
  if (muted) return;
  const ctx = ensureContext();
  if (!ctx) return;
  resumeIfNeeded(ctx);

  // Very neutral low tone — used at the "wrong again, here's the correct
  // spelling" moment. Spec §8 explicitly allows silence here; this is
  // the quietest acceptable cue, more "soft thud" than "ding".
  const now = ctx.currentTime;
  spawnTone(ctx, { freq: 220, type: "sine", peak: 0.06, attack: 0.03, decay: 0.32, start: now });
}

// ─── Internals ───────────────────────────────────────────────────────

let audioContext: AudioContext | null = null;
let unlocked = false;

function ensureContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (audioContext) return audioContext;
  // Safari ships under the webkit prefix on some older builds; the union
  // covers both surfaces without leaning on `any`.
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  try {
    audioContext = new Ctor();
  } catch {
    return null;
  }
  return audioContext;
}

/**
 * Resume the context if it isn't currently running. Catches both
 * 'suspended' (pre-gesture) and 'interrupted' (post-SpeechSynthesis on
 * iOS). Fires resume() synchronously and doesn't await it — Safari's
 * gesture-window check passes, and scheduled oscillators play once
 * the context transitions to 'running'.
 */
function resumeIfNeeded(ctx: AudioContext): void {
  if (ctx.state !== "running") {
    void ctx.resume().catch(() => {
      // Pre-gesture Safari: cue won't play this once. The unlockAudio
      // listener will catch the next gesture and prime the session.
    });
  }
}

/**
 * iOS Safari (and iPad Chrome, which runs on WebKit) unlock. Runs
 * once on the FIRST user gesture anywhere on the page. Two parallel
 * unlock paths — both needed for iPad reliability:
 *
 *   1. **AudioContext path**: create the context, call resume()
 *      synchronously inside the gesture, and play a 1-sample silent
 *      buffer. This is the standard Web Audio unlock.
 *
 *   2. **HTML5 `<audio>` path**: play a tiny silent WAV through an
 *      `<audio>` element. This is what tells iOS to put the audio
 *      session into the "playback" category (vs the default
 *      "ambient" which can be muted by silent switch / focus mode).
 *      Web Audio alone doesn't reliably set the category on iPad —
 *      Howler.js and similar libraries also use this belt-and-braces
 *      approach.
 *
 * Either path succeeding is enough for cues to play; running both
 * costs nothing extra and covers more device/setting combinations.
 *
 * No-op on browsers that don't need it (Chrome desktop, Firefox) —
 * they'll get a silent buffer + silent audio they don't notice.
 */
function unlockAudio(): void {
  if (unlocked) return;
  unlocked = true;

  // Path 1: AudioContext.
  const ctx = ensureContext();
  if (ctx) {
    // Sync resume — must be in the gesture stack.
    void ctx.resume().catch(() => {});
    try {
      const buffer = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(ctx.destination);
      src.start(0);
    } catch {
      // If buffer creation/start fails, the cue functions still call
      // resumeIfNeeded() each time and will recover on the next
      // gesture.
    }
  }

  // Path 2: HTML5 `<audio>` element. Sets iOS audio session
  // category to "playback" so subsequent Web Audio cues actually
  // produce sound through the speakers. Without this, Web Audio
  // can stay silent on iPad even with the context resumed.
  try {
    const audio = new Audio(SILENT_WAV_DATA_URL);
    audio.volume = 0;
    audio.preload = "auto";
    // play() must be in the gesture stack; the returned Promise
    // resolves later, but the gesture-check has already passed by
    // the time we call it.
    void audio.play().catch(() => {});
    // After playback starts (or the brief silent audio finishes),
    // free the element so we don't hold the audio session open.
    audio.addEventListener(
      "ended",
      () => {
        audio.src = "";
      },
      { once: true },
    );
  } catch {
    // Audio element creation can throw on very old browsers; nothing
    // to do but rely on path 1.
  }
}

/**
 * Tiny silent WAV (44 bytes header + 0 sample data). Embedded as a
 * data URL so we don't ship a separate asset for this. Wavefile spec:
 * RIFF header, fmt chunk (PCM 8kHz 8-bit mono), empty data chunk.
 * iOS will happily play it and the act of playing is what flips the
 * audio session category — the actual sound is irrelevant.
 */
const SILENT_WAV_DATA_URL =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";

// Register the unlock listener at module load. `once: true` so it
// auto-removes after the first fire. Listening on all three event
// types catches keyboard-only (parent settings), touch (Eva on iPad),
// and mouse (parent on desktop) without doubling up — `once: true` on
// each removes itself, and the second event won't re-trigger because
// `unlocked` short-circuits.
if (typeof document !== "undefined") {
  const opts = { once: true, passive: true } as const;
  document.addEventListener("touchstart", unlockAudio, opts);
  document.addEventListener("mousedown", unlockAudio, opts);
  document.addEventListener("keydown", unlockAudio, opts);
}

interface ToneOpts {
  freq: number;
  type: OscillatorType;
  peak: number;
  attack: number;
  decay: number;
  start: number;
}

function spawnTone(ctx: AudioContext, opts: ToneOpts): void {
  const osc = ctx.createOscillator();
  osc.type = opts.type;
  osc.frequency.setValueAtTime(opts.freq, opts.start);
  const gain = ctx.createGain();
  osc.connect(gain).connect(ctx.destination);
  applyEnvelope(ctx, gain, { attack: opts.attack, hold: 0, decay: opts.decay, peak: opts.peak }, opts.start);
  osc.start(opts.start);
  osc.stop(opts.start + opts.attack + opts.decay + 0.05);
}

interface EnvelopeOpts {
  attack: number;
  hold: number;
  decay: number;
  peak: number;
}

function applyEnvelope(
  ctx: AudioContext,
  gain: GainNode,
  opts: EnvelopeOpts,
  startAt: number = ctx.currentTime,
): void {
  const { attack, hold, decay, peak } = opts;
  gain.gain.cancelScheduledValues(startAt);
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(peak, startAt + attack);
  gain.gain.setValueAtTime(peak, startAt + attack + hold);
  // exponentialRampToValueAtTime can't target 0; 0.001 is the standard
  // "near-silence" floor used by every Web Audio tutorial.
  gain.gain.exponentialRampToValueAtTime(0.001, startAt + attack + hold + decay);
}

function makeNoise(ctx: AudioContext, durationSec: number): AudioBufferSourceNode {
  const sampleRate = ctx.sampleRate;
  const buffer = ctx.createBuffer(1, Math.floor(sampleRate * durationSec), sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  return src;
}
